import { useState } from 'react';
import './UnlockModal.css';

interface UnlockModalProps {
  pageId: number;
  unlockPrice: string;
  hasPassword: boolean;
  onUnlockWithPayment: (pageId: number) => Promise<boolean>;
  onUnlockWithPassword: (pageId: number, password: string) => Promise<boolean>;
  onClose: () => void;
}

export function UnlockModal({
  pageId,
  unlockPrice,
  hasPassword,
  onUnlockWithPayment,
  onUnlockWithPassword,
  onClose,
}: UnlockModalProps) {
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'payment' | 'password'>(
    parseFloat(unlockPrice) > 0 ? 'payment' : 'password'
  );

  const handlePaymentUnlock = async () => {
    setUnlocking(true);
    setError('');
    try {
      const success = await onUnlockWithPayment(pageId);
      if (success) {
        onClose();
      } else {
        setError('Failed to unlock. Please try again.');
      }
    } catch (err) {
      setError('Transaction failed');
    } finally {
      setUnlocking(false);
    }
  };

  const handlePasswordUnlock = async () => {
    if (!password) {
      setError('Please enter a password');
      return;
    }

    setUnlocking(true);
    setError('');
    try {
      const success = await onUnlockWithPassword(pageId, password);
      if (success) {
        onClose();
      } else {
        setError('Incorrect password');
      }
    } catch (err) {
      setError('Failed to unlock');
    } finally {
      setUnlocking(false);
    }
  };

  const showPayment = parseFloat(unlockPrice) > 0;
  const showPassword = hasPassword;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          ✕
        </button>

        <h2 className="modal-title">Unlock Secret Page</h2>
        <p className="modal-subtitle">Page #{pageId}</p>

        {showPayment && showPassword && (
          <div className="unlock-tabs">
            <button
              className={`tab ${activeTab === 'payment' ? 'active' : ''}`}
              onClick={() => setActiveTab('payment')}
            >
              Pay to Unlock
            </button>
            <button
              className={`tab ${activeTab === 'password' ? 'active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              Use Password
            </button>
          </div>
        )}

        <div className="unlock-options">
          {activeTab === 'payment' && showPayment && (
            <div className="unlock-option">
              <div className="option-header">
                <span className="bitcoin-icon">₿</span>
                <h3>Pay with cBTC</h3>
              </div>
              <div className="price-display">
                <span className="price-amount">{unlockPrice}</span>
                <span className="price-currency">cBTC</span>
              </div>
              <p className="option-description">
                Unlock this page by paying the owner. Once paid, you'll have permanent access.
              </p>
              <button
                className="unlock-action-button payment"
                onClick={handlePaymentUnlock}
                disabled={unlocking}
              >
                {unlocking ? 'Processing...' : `Pay ${unlockPrice} cBTC`}
              </button>
            </div>
          )}

          {activeTab === 'password' && showPassword && (
            <div className="unlock-option">
              <div className="option-header">
                <span className="key-icon">🔑</span>
                <h3>Enter Password</h3>
              </div>
              <p className="option-description">
                If you have the password, enter it below to unlock for free.
              </p>
              <input
                type="password"
                className="password-input"
                placeholder="Enter password..."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordUnlock()}
              />
              <button
                className="unlock-action-button password"
                onClick={handlePasswordUnlock}
                disabled={unlocking || !password}
              >
                {unlocking ? 'Verifying...' : 'Unlock with Password'}
              </button>
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
}
